/**
 * @file Short-lived status marker floating over an entity (GDD §Fase 4).
 *
 * Pure timing data: which marker, how long it lives, how high it sits. The
 * billboard itself is owned by
 * {@link ../systems/VisualIndicatorSystem.js VisualIndicatorSystem} — components
 * never hold Three.js objects (that privilege belongs to
 * {@link ./Renderable.js Renderable} alone).
 *
 * Adding a second marker (low health, stunned, boss phase) is a new `kind`
 * string plus one texture in the system: nothing here has to change.
 */

import { Component } from '../ecs/Component.js';

/**
 * @readonly
 * @enum {string}
 */
export const IndicatorKind = Object.freeze({
  /** The "!" shown the moment an enemy notices the player. */
  ALERT: 'alert'
});

export class VisualIndicator extends Component {
  /**
   * @param {object} [options] Marker setup.
   * @param {string} [options.kind] One of {@link IndicatorKind}.
   * @param {number} [options.duration] Seconds the marker stays on screen.
   * @param {number} [options.yOffset] Height above the entity's transform.
   * @param {number} [options.scale] World-space size of the billboard.
   */
  constructor({ kind = IndicatorKind.ALERT, duration = 0.8, yOffset = 1.6, scale = 0.8 } = {}) {
    super();
    /** @type {string} */
    this.kind = kind;
    /** @type {number} */
    this.duration = duration;
    /** @type {number} Seconds left before the marker is dropped. */
    this.remaining = duration;
    /** @type {number} */
    this.yOffset = yOffset;
    /** @type {number} */
    this.scale = scale;
  }

  /**
   * Replays the marker from the top, reusing the same billboard. Called when an
   * entity re-triggers an indicator it already owns, which is what keeps a
   * second trigger from stacking two markers on one head.
   * @returns {void}
   */
  restart() {
    this.remaining = this.duration;
  }

  /** @returns {void} */
  reset() {
    this.remaining = this.duration;
  }
}
